<template>
  <div id="reservationSection" class="columns-container container-spacing">
    <div class="column">
      <div class="change-order-page__header-left section-header">
        <h3>
          {{ i18n.t('/change-order/sections/reservation') }}
        </h3>
      </div>
      <div class="hold-data-input-container">
        <div v-if="hasOriginalHolds">
          <span>
            <strong>
              {{ i18n.t('/change-order/reservation/active') }}
            </strong>
          </span>
          <ul class="original-holds-container">
            <li v-if="hasReservation">
              <p>
                {{ i18n.t('/change-order/reservation/reserved') }}
                <template v-if="getReservationStartDate"
                  >van {{ getReservationStartDate }}</template
                >
                t/m {{ getReservationEndDate }}
              </p>
              <p v-if="getOriginalReservingDealerNumber">
                {{ getOriginalReservingDealerNumber }}
              </p>
            </li>
            <li v-if="hasOriginalStockHold">
              {{ i18n.t('/change-order/reservation/stock-hold') }}
              {{ stockExpiryDate }}
            </li>
            <li v-if="hasOriginalShowroomHold">
              {{ i18n.t('/change-order/reservation/showroom-hold') }}
              {{ originalShowroomExpiryDate }}
            </li>
          </ul>
        </div>
        <div v-else>
          <span>
            <strong>
              {{ i18n.t('/change-order/reservation/no-reservation') }}
            </strong>
          </span>
        </div>
      </div>
    </div>
    <div class="column">
      <div class="change-order-page__header-right section-header">
        <h3>
          {{ i18n.t('/change-order/sections/reservation') }}
        </h3>
      </div>
      <div class="hold-data-input-container">
        <div v-if="!canShowAddButton && showInput">
          <div class="hold-input-panel">
            <label class="label">
              {{ i18n.t('/change-order/reservation/reservation-type') }}
            </label>
            <br />
            <div>
              <div class="reservation-row">
                <div class="reservation-column">
                  <form-checkbox id="reservation-0" title="Reserveren">
                    <input
                      id="reservation-0"
                      v-model="tempChangedIsReserved"
                      type="checkbox"
                      name="reservation-status"
                      :disabled="!canChangeReservation || null"
                    />
                  </form-checkbox>
                </div>
              </div>
              <div v-if="tempChangedIsReserved" class="reservation-sub-items">
                <div class="reservation-sub-item">
                  <label class="label" for="dealers">Reserveren voor</label>
                  <select
                    id="dealers"
                    v-model="reservedForDealer"
                    :disabled="!canChangeReservation || null"
                  >
                    <option
                      v-for="establishment in availableEstablishments"
                      :key="establishment.Id"
                      :value="establishment.Id"
                    >
                      {{ establishment.Name }}
                    </option>
                  </select>
                </div>
                <div v-if="dealerSettings.isImporter" class="reservation-sub-item">
                  <date-picker
                    id="reservationEndDate"
                    :date="tempChangedReservationEnd"
                    :allow-empty="true"
                    :max-date="maxReservationEndDate"
                    title="Einddatum"
                    @change-date="changeReservationEndDate"
                  />
                </div>
              </div>
              <div class="reservation-row">
                <div class="reservation-column reservation-column--with-lock">
                  <form-checkbox id="reservation-1" title="Blokkeren">
                    <input
                      id="reservation-1"
                      v-model="tempChangedStockHold"
                      type="checkbox"
                      name="reservation-status"
                      :disabled="!canChangeStockHold || null"
                    />
                  </form-checkbox>
                  <span v-if="!canChangeStockHold" class="edit-icon">
                    <i class="fa-solid fa-lock" :title="stockHoldLockedReason"></i>
                  </span>
                </div>
              </div>
              <div class="reservation-row">
                <div class="reservation-column reservation-column--with-lock">
                  <form-checkbox id="reservation-2" title="Blokkeren showroom">
                    <input
                      id="reservation-2"
                      v-model="tempChangedShowroomHold"
                      type="checkbox"
                      name="reservation-status"
                      :disabled="!canChangeShowRoomHold || null"
                    />
                  </form-checkbox>
                  <span v-if="!canChangeShowRoomHold" class="edit-icon">
                    <i class="fa-solid fa-lock" :title="showroomHoldLockedReason"></i>
                  </span>
                </div>
                <div
                  v-if="tempChangedShowroomHold"
                  :disabled="!canChangeShowRoomHold || null"
                  class="reservation-column"
                >
                  <date-picker
                    id="showroomHoldEndDate"
                    :date="showroomHoldEndDate"
                    :allow-empty="true"
                    title=""
                    @change-date="changeShowroomHoldEndDate"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="hold-order-button-panel">
            <app-button data-qa="confirm-K-code" @click="confirmHold">
              {{ i18n.t('/common/confirm') }}
            </app-button>
            <app-button data-qa="cancel-Add-Mode" @click="cancelAddMode">
              {{ i18n.t('/common/cancel') }}
            </app-button>
          </div>
        </div>
        <div v-if="!showInput">
          <span
            v-if="hasChangedHoldsOrReservation && !canShowAddButton"
            class="confirmation-message"
          >
            <i class="fas fa-arrows-rotate"></i>&nbsp;
            <strong>
              {{ i18n.t('/change-order/generic/modified') }}
            </strong>
            &nbsp; {{ i18n.t('/change-order/generic/modified-confirmation') }}
          </span>

          <ul class="changed-holds-container">
            <li v-if="hasChangedReservation">
              <p>
                {{ i18n.t('/change-order/reservation/reserved') }}
              </p>
              <p v-if="getChangedReservingDealerNumber">
                {{ getChangedReservingDealerNumber }}
              </p>
              <p v-if="getChangedReservationEndDate">t/m {{ getChangedReservationEndDate }}</p>
            </li>
            <li v-if="hasChangedStockHold">
              {{ i18n.t('/change-order/reservation/stock-hold') }}
            </li>
            <li v-if="hasChangedShowroomHold">
              {{ i18n.t('/change-order/reservation/showroom-hold') }}
              {{ changedShowroomExpiryDate }}
            </li>
          </ul>

          <div v-if="!canShowAddButton" class="hold-button-panel">
            <app-button
              v-if="isChangeButtonVisible"
              data-qa="enter-edit-mode"
              :title="retrieveLockedReasons"
              @click="enterEditMode"
            >
              <i :class="determineChangedClass"></i>&nbsp;
              {{ i18n.t('/edit button label') }}
            </app-button>
            <app-button
              v-if="isChangeButtonVisible && holdOptionChanged"
              data-qa="remove-K-code"
              :title="retrieveLockedReasons"
              @click="removeHolds"
            >
              <i :class="determineChangedClass"></i>
              {{ i18n.t('/change-order/actions/delete') }}
            </app-button>
          </div>
        </div>
        <div v-if="canShowAddButton">
          <app-button
            v-if="isChangeButtonVisible"
            data-qa="enter-Add-Mode"
            :title="retrieveLockedReasons"
            @click="enterAddMode"
          >
            <i :class="determineChangedClass"></i>
            {{ i18n.t('/change-order/reservation/make-reservation') }}
          </app-button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import { mapGetters } from 'vuex'
import DatePicker from '@/components/generic/presentational/atoms/datePicker/DatePicker.vue'
import StoreConnector from '@/helpers/StoreConnector'
import moment from 'moment'
import ChangeOrderStatus from '@/helpers/enums/changeOrderStatus'
import FormCheckbox from '@/components/generic/presentational/atoms/formCheckbox/FormCheckbox.vue'

export default {
  components: {
    DatePicker,
    AppButton,
    FormCheckbox,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showAddButton: true,
      showInput: false,
      holdOptionChanged: null,
      reservedForDealer: null,
      canSelectedEndDate: false,
      showroomHoldEndDate: null,
      establishments: [],
      tempChangedIsReserved: false,
      tempChangedStockHold: false,
      tempChangedShowroomHold: false,
      tempChangedReservationEnd: null,
    }
  },
  computed: {
    ...mapGetters({
      changeOrder: 'changeOrder/get',
      dealerSettings: 'user/dealerSettings',
    }),
    isChangeButtonVisible() {
      return this.dealerSettings.isImporter
    },
    getShowroomHoldEndDate() {
      return this.formatDate(this.showroomHoldEndDate)
    },
    getReservationStartDate() {
      return this.changeOrder.Reservation.OriginalReservationStart
    },
    getReservationEndDate() {
      return this.changeOrder.Reservation.OriginalReservationEnd
    },
    getChangedReservationEndDate() {
      return this.changeOrder.Reservation.ChangedReservationEnd
    },
    maxReservationEndDate() {
      return moment().add(6, 'months')
    },
    getOriginalReservingDealerNumber() {
      let originalReservingDealerNumber = this.changeOrder.Reservation.OriginalReservingDealerNumber
      if (originalReservingDealerNumber) {
        return 'Gereserveerd door: ' + originalReservingDealerNumber
      }

      return null
    },
    getChangedReservingDealerNumber() {
      let changedReservingDealerNumber = this.changeOrder.Reservation.ChangedReservingDealerNumber
      if (changedReservingDealerNumber) {
        let changedReservingDealerName =
          this.getChangedReservingEstablishmentName || changedReservingDealerNumber
        return 'Gereserveerd door: ' + changedReservingDealerName
      }

      return null
    },
    getChangedReservingEstablishmentName() {
      if (this.availableEstablishments) {
        return this.availableEstablishments.find(
          (e) => e.Id === this.changeOrder.Reservation.ChangedReservingDealerNumber
        )?.Name
      }

      return null
    },
    hasOriginalShowroomHold() {
      return this.changeOrder.Holds.ShowroomHold.OriginalShowroomHold === true
    },
    hasChangedShowroomHold() {
      return this.changeOrder.Holds.ShowroomHold.ChangedShowroomHold === true
    },
    stockExpiryDate() {
      if (this.hasOriginalStockHold) {
        let originalStockHoldExpiryDate =
          this.changeOrder.Holds.StockHold.OriginalStockHoldExpiryDate

        if (originalStockHoldExpiryDate) {
          return ' tot ' + originalStockHoldExpiryDate
        }
      }

      return ''
    },
    originalShowroomExpiryDate() {
      if (this.hasOriginalShowroomHold) {
        let originalShowroomHoldExpiryDate =
          this.changeOrder.Holds.ShowroomHold.OriginalShowroomHoldExpiryDate

        if (originalShowroomHoldExpiryDate) {
          return ' t/m ' + originalShowroomHoldExpiryDate
        }
      }

      return ''
    },
    changedShowroomExpiryDate() {
      if (this.hasChangedShowroomHold) {
        let changedShowroomHoldExpiryDate =
          this.changeOrder.Holds.ShowroomHold.ChangedShowroomHoldExpiryDate

        if (changedShowroomHoldExpiryDate) {
          return ' t/m ' + changedShowroomHoldExpiryDate
        }
      }

      return ''
    },
    availableEstablishments() {
      if (this.establishments) {
        return this.establishments.map((establishment) => {
          return {
            Id: establishment.Id,
            Name: `${establishment.Id} - ${establishment.Name} - ${establishment.City}`,
          }
        })
      }
      return null
    },
    hasReservation() {
      return this.changeOrder.Reservation?.OriginalIsReserved === true
    },
    hasChangedReservation() {
      return this.changeOrder.Reservation?.ChangedIsReserved === true
    },
    hasOriginalStockHold() {
      return this.changeOrder.Holds?.StockHold?.OriginalStockHold === true
    },
    hasChangedStockHold() {
      return this.changeOrder.Holds?.StockHold?.ChangedStockHold === true
    },
    hasOriginalHolds() {
      return this.hasReservation || this.hasOriginalShowroomHold || this.hasOriginalStockHold
    },
    holdOptions() {
      return [
        {
          /* Reservation API  */
          Id: 0,
          Name: 'Reserveren',
        },
        {
          /* Stock Hold API  */
          Id: 1,
          Name: 'Blokkeren',
        },
        {
          /* Showroom Hold  API*/
          Id: 2,
          Name: 'Blokkeren showroom',
        },
        {
          Id: null,
          Name: 'Vrijgeven reservering/blokkade',
        },
      ]
    },
    canSelectDealer() {
      return this.hasChangedReservation /* Reservation API  */
    },
    canSetEndDate() {
      return this.hasChangedShowroomHold /* Showroom Hold  API*/
    },
    canChangeHolds() {
      // Check if any of the order statuses are submitted or archived
      const isOrderStatusValid =
        this.changeOrder.Reservation?.OrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.ShowroomHold?.OrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.StockHold?.OrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.archived

      // Check if permissions allow changes
      const canChangeReservation = this.canChangeReservation
      const canChangeShowRoomHold = this.canChangeShowRoomHold
      const canChangeStockHold = this.canChangeStockHold

      return (
        isOrderStatusValid && (canChangeReservation || canChangeShowRoomHold || canChangeStockHold)
      )
    },
    canChangeReservation() {
      return this.changeOrder.Permissions?.CanChangeReservations?.Allowed === true
    },
    canChangeShowRoomHold() {
      return this.changeOrder.Permissions?.CanChangeHoldShowroom?.Allowed === true
    },
    showroomHoldLockedReason() {
      return this.changeOrder.Permissions?.CanChangeHoldShowroom?.Reason
    },
    canChangeStockHold() {
      return this.changeOrder.Permissions?.CanChangeHoldStock?.Allowed === true
    },
    stockHoldLockedReason() {
      return this.changeOrder.Permissions?.CanChangeHoldStock?.Reason
    },
    canShowAddButton() {
      return this.showAddButton
    },
    hasChangedHoldsOrReservation() {
      let originalIsReserved = this.changeOrder.Reservation.OriginalIsReserved
      let changedIsReserved = this.changeOrder.Reservation.ChangedIsReserved

      let originalShowroomHold = this.changeOrder.Holds.ShowroomHold.OriginalShowroomHold
      let changedShowroomHold = this.changeOrder.Holds.ShowroomHold.ChangedShowroomHold

      let originalShowroomHoldExpiryDate =
        this.changeOrder.Holds.ShowroomHold.OriginalShowroomHoldExpiryDate

      let changedShowroomHoldExpiryDate =
        this.changeOrder.Holds.ShowroomHold.ChangedShowroomHoldExpiryDate

      let originalStockHold = this.changeOrder.Holds.StockHold.OriginalStockHold
      let changedStockHold = this.changeOrder.Holds.StockHold.ChangedStockHold

      return (
        originalIsReserved !== changedIsReserved ||
        originalShowroomHold !== changedShowroomHold ||
        originalShowroomHoldExpiryDate !== changedShowroomHoldExpiryDate ||
        originalStockHold !== changedStockHold ||
        this.changeOrder.Reservation.OriginalReservationEnd !==
          this.changeOrder.Reservation.ChangedReservationEnd
      )
    },
    retrieveLockedReasons() {
      let lockedReasons = []
      if (!this.canChangeHolds) {
        if (!this.changeOrder.Permissions?.CanChangeReservations?.Allowed) {
          lockedReasons.push(this.changeOrder.Permissions?.CanChangeReservations?.Reason)
        }
        if (!this.changeOrder.Permissions?.CanChangeHoldShowroom?.Allowed) {
          lockedReasons.push(this.changeOrder.Permissions?.CanChangeHoldShowroom?.Reason)
        }
        if (!this.changeOrder.Permissions?.CanChangeHoldStock?.Allowed) {
          lockedReasons.push(this.changeOrder.Permissions?.CanChangeHoldStock?.Reason)
        }
        if (
          this.changeOrder.Reservation.OrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.Holds?.ShowroomHold?.OrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.Holds?.StockHold?.OrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.MainOrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.MainOrderStatus === ChangeOrderStatus.archived
        ) {
          lockedReasons.push('Order has been submitted or deleted')
        }
      }
      return lockedReasons.join('\n')
    },
    determineChangedClass() {
      if (this.canChangeHolds) {
        return 'fa-solid fa-pencil'
      } else {
        return 'fa-solid fa-lock'
      }
    },
  },
  created() {
    StoreConnector.get('changeOrder/establishments', (data) => {
      this.establishments = data
    })

    StoreConnector.dispatch('changeOrder/getEstablishments')
  },
  mounted() {
    this.setHolds()
  },
  methods: {
    setHolds() {
      this.tempChangedIsReserved = this.changeOrder.Reservation.ChangedIsReserved
      this.tempChangedStockHold = this.changeOrder.Holds.StockHold.ChangedStockHold
      this.tempChangedShowroomHold = this.changeOrder.Holds.ShowroomHold.ChangedShowroomHold

      if (this.changeOrder.Reservation.ChangedReservingDealerNumber) {
        this.reservedForDealer = this.changeOrder.Reservation.ChangedReservingDealerNumber
      }

      const changedReservationEnd = this.changeOrder.Reservation.ChangedReservationEnd
      this.tempChangedReservationEnd = changedReservationEnd
        ? moment.utc(changedReservationEnd, 'DD-MM-YYYY')
        : null
      if (this.changeOrder.Holds.ShowroomHold.ChangedShowroomHold === true) {
        let changedShowroomHoldExpiryDate =
          this.changeOrder.Holds.ShowroomHold.ChangedShowroomHoldExpiryDate
        if (changedShowroomHoldExpiryDate) {
          this.showroomHoldEndDate = moment.utc(changedShowroomHoldExpiryDate, 'DD-MM-YYYY')
        } else {
          this.showroomHoldEndDate = null
        }
      }
    },
    changeShowroomHoldEndDate(value) {
      this.showroomHoldEndDate = value
    },
    changeReservationEndDate(value) {
      this.tempChangedReservationEnd = value
    },
    formatDate(date) {
      return moment.utc(date).format('DD-MM-YYYY')
    },
    isOptionEnabled(optionId) {
      switch (optionId) {
        case 0:
          return this.canChangeReservation
        case 1:
          return this.canChangeStockHold
        case 2:
          return this.canChangeShowRoomHold
        default:
          return true
      }
    },
    enterAddMode() {
      if (this.canChangeHolds) {
        this.showAddButton = false
        this.showInput = true
      }
    },
    cancelAddMode() {
      this.showAddButton = true
      this.showInput = false
      this.setHolds()
    },
    confirmHold() {
      this.changeOrder.Holds.StockHold.ChangedStockHold = this.tempChangedStockHold
      this.changeOrder.Holds.ShowroomHold.ChangedShowroomHold = this.tempChangedShowroomHold
      this.changeOrder.Reservation.ChangedIsReserved = this.tempChangedIsReserved

      /* Reservation should only set the ChangedReservingDealerNumber when checkbox checked */
      if (this.tempChangedIsReserved) {
        this.changeOrder.Reservation.ChangedReservingDealerNumber = this.reservedForDealer
        this.changeOrder.Reservation.ChangedReservationEnd = this.tempChangedReservationEnd
          ? this.formatDate(this.tempChangedReservationEnd)
          : null
      } else {
        this.changeOrder.Reservation.ChangedReservingDealerNumber = null
        this.changeOrder.Reservation.ChangedReservationEnd = null
      }

      /* Showroom Hold */
      if (this.changeOrder.Holds.ShowroomHold.ChangedShowroomHold) {
        if (this.showroomHoldEndDate) {
          this.changeOrder.Holds.ShowroomHold.ChangedShowroomHoldExpiryDate = this.formatDate(
            this.showroomHoldEndDate
          )
        } else {
          this.changeOrder.Holds.ShowroomHold.ChangedShowroomHoldExpiryDate = null
        }
      } else {
        this.changeOrder.Holds.ShowroomHold.ChangedShowroomHoldExpiryDate =
          this.changeOrder.Holds.ShowroomHold.OriginalStockHoldExpiryDate
      }

      this.$store.commit('changeOrder/setReservationStatus')
      this.showInput = false
    },

    enterEditMode() {
      this.enterAddMode()
    },

    removeHolds() {
      if (this.canChangeHolds) {
        this.$store.commit('changeOrder/setReservationStatus')

        this.reservedForDealer = null
        this.canSelectedEndDate = false

        this.changeOrder.Reservation.ChangedIsReserved = false
        this.changeOrder.Reservation.ChangedReservingDealerNumber = null
        this.changeOrder.Holds.StockHold.ChangedStockHold = false
        this.changeOrder.Holds.ShowroomHold.ChangedShowroomHold = false

        this.$store.commit('changeOrder/setReservationStatus')

        this.showAddButton = true
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.columns-container {
  display: flex;
  justify-content: space-between;
}

.container-spacing {
  margin-top: 50px;
}

.column {
  flex: 1;
  margin: 0 20px;
}

h3 {
  font-weight: 400;
  margin: 0;
}

.change-order {
  &-page__header-left {
    h3 {
      color: $black;
    }
  }

  &-page__header-right {
    h3 {
      color: $color-primary;
    }
  }
}

.section-header {
  border-bottom: 1px solid #a3b8c0;
  align-items: center;
  margin-bottom: 1rem;
}

.hold-data-input-container {
  margin-top: 40px;
}

.confirmation-message {
  background-color: #ffdd9c;
  font-size: 18px;
  padding: 10px;
}

.hold-input-panel {
  label {
    font-weight: bold;
    margin-bottom: 3px;
  }
  select {
    width: 400px;
    border: 1px solid black;
  }

  > div {
    display: flex;
    flex-direction: column;

    > div {
      flex: 1;
    }
  }
}

.hold-order-button-panel {
  margin-top: 20px;
}

.hold-panel {
  margin-top: 20px;
  span {
    padding: 5px;
  }
}

.hold-info {
  margin-bottom: 2px;
}

.hold-button-panel {
  margin-top: 20px;
}

.edit-icon {
  margin-left: 10px;
  cursor: pointer;
}

.reservation-row {
  display: flex;
  flex-direction: row;
}

.reservation-column {
  padding: 5px;

  &--with-lock {
    display: flex;
    align-items: center;
  }
}

.reservation-sub-items {
  margin-left: 28px;
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: flex-end;
}

.reservation-sub-item {
  display: flex;
  flex-direction: column;

  .label {
    font-weight: bold;
    margin-bottom: 3px;
  }

  select {
    width: 400px;
    border: 1px solid black;
  }
}

.radio-end-date-selection {
  margin-bottom: 5px;
}
.original-holds-container,
.changed-holds-container {
  margin-top: 10px;
  margin-left: 30px;
}

.disabled-option {
  color: gray;
  font-style: italic;
  cursor: not-allowed;
}

.disabled-option i {
  margin-right: 5px;
}

select option:disabled {
  background-color: #f0f0f0;
  color: gray;
  font-style: italic;
}
</style>
